import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, ChevronsUpDown, CheckSquare, Square, type LucideIcon } from 'lucide-react';
import baseapi from '../services/baseapi';

interface DropdownItem {
    id?: string | number | null;
    [key: string]: any;
}

interface MultiSelectApiDropdownWithAllProps {
    label?: string;
    value?: DropdownItem[];
    onChange: (items: DropdownItem[]) => void;
    disabled?: boolean;
    placeholder?: string;
    className?: string;
    apiEndpoint: string;
    icon?: LucideIcon | null;
    searchPlaceholder?: string;
    searchInputPlaceholder?: string;
    error?: string;
    required?: boolean;
    displayProperty: string;
    secondaryDisplayProperty?: string;
    searchFields: string[];
    additionalQueries?: Record<string, any>;
    additionalParams?: Record<string, any>;
    getDisplayText?: (item: DropdownItem) => string;
    clearDisabled?: boolean;
    idProperty?: string;
    dataPath?: string;
    maxVisibleTags?: number;
    selectAll?: boolean;
    selectall?: boolean;
    buttonHeight?:number
}

const MultiSelectApiDropdownWithAll = React.memo<MultiSelectApiDropdownWithAllProps>(({
    label,
    value = [],
    onChange,
    disabled = false,
    placeholder = "Select items",
    className = "",
    apiEndpoint,
    icon: Icon = null,
    searchPlaceholder = 'Search',
    searchInputPlaceholder = 'Type to search...',
    error,
    required,
    displayProperty,
    secondaryDisplayProperty,
    searchFields,
    additionalQueries = {},
    additionalParams = {},
    getDisplayText,
    clearDisabled = false,
    idProperty = 'id',
    dataPath,
    maxVisibleTags = 3,
    selectAll = false,
    selectall = false,
    buttonHeight = 44
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
    const [items, setItems] = useState<DropdownItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showSearch, setShowSearch] = useState(false);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);

    // Memoize additionalQueries to prevent infinite loops
    const memoizedAdditionalQueries = useRef(additionalQueries);
    const memoizedAdditionalParams = useRef(additionalParams);

    useEffect(() => {
        if (JSON.stringify(memoizedAdditionalQueries.current) !== JSON.stringify(additionalQueries)) {
            memoizedAdditionalQueries.current = additionalQueries;
        }
    }, [additionalQueries]);

    useEffect(() => {
        if (JSON.stringify(memoizedAdditionalParams.current) !== JSON.stringify(additionalParams)) {
            memoizedAdditionalParams.current = additionalParams;
        }
    }, [additionalParams]);

    // Calculate dropdown position when opening
    const calculateDropdownPosition = useCallback(() => {
        if (!buttonRef.current) return;
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const dropdownHeight = 280; // Adjusted for compact height
        const windowHeight = window.innerHeight;
        const spaceBelow = windowHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;

        let position: 'bottom' | 'top' = 'bottom';
        let style: React.CSSProperties = {
            width: `${buttonRect.width}px`,
            left: `${buttonRect.left}px`,
            position: 'fixed'
        };

        if (spaceBelow < dropdownHeight + 20 && spaceAbove > dropdownHeight + 20) {
            position = 'top';
            style.bottom = `${windowHeight - buttonRect.top + 8}px`;
        } else {
            position = 'bottom';
            style.top = `${buttonRect.bottom + 8}px`;
        }

        setDropdownPosition(position);
        setMenuStyle(style);
    }, []);

    const updatePosition = useCallback(() => {
        if (isOpen) {
            calculateDropdownPosition();
        }
    }, [isOpen, calculateDropdownPosition]);

    useEffect(() => {
        if (isOpen) {
            window.addEventListener('resize', updatePosition);
            window.addEventListener('scroll', updatePosition, true);
        }
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isOpen, updatePosition]);

    // Debounce search function
    const debounce = useCallback((func: Function, delay: number) => {
        let timeoutId: number;
        return (...args: any[]) => {
            clearTimeout(timeoutId);
            timeoutId = window.setTimeout(() => func.apply(null, args), delay);
        };
    }, []);

    // Get nested property value using dot notation
    const getNestedProperty = useCallback((obj: any, path: string) => {
        return path.split('.').reduce((current, key) => current?.[key], obj);
    }, []);

    // Get ID from item using configurable idProperty
    const getItemId = useCallback((item: DropdownItem) => {
        if (typeof item === 'string' || typeof item === 'number') return item;
        if (!item || typeof item !== 'object') return item;
        return getNestedProperty(item, idProperty);
    }, [getNestedProperty, idProperty]);

    // API call to fetch items
    const fetchItems = useCallback(async (search: string = '') => {
        if (showSearch && search.trim() === '') {
            setItems([]);
            return;
        }
        setIsLoading(true);
        try {
            const queryObj: Record<string, any> = { ...memoizedAdditionalQueries.current };
            if (search.trim()) {
                const searchConditions = searchFields.map(field => ({
                    [field]: { $ilike: `%${search}%` }
                }));
                queryObj.$or = searchConditions;
            }
            const params = {
                query: JSON.stringify(queryObj),
                limit: search.trim() ? 10 : 20,
                ...memoizedAdditionalParams.current
            };
            const response = await baseapi.get(apiEndpoint, { params });
            if (response.data.status) {
                let transformedItems = response.data.data || [];
                if (dataPath) {
                    transformedItems = getNestedProperty(response.data.data, dataPath) || [];
                }
                setItems(transformedItems);
            }
        } catch (error) {
            console.error(`Error fetching data from ${apiEndpoint}:`, error);
            setItems([]);
        } finally {
            setIsLoading(false);
        }
    }, [apiEndpoint, searchFields, showSearch]);

    // Debounced search function
    const debouncedSearch = useCallback(
        debounce((term: string) => {
            fetchItems(term);
        }, 300),
        [fetchItems]
    );

    // Single fetch only on dropdown open
    const prevIsOpen = useRef(false);
    useEffect(() => {
        if (isOpen && !prevIsOpen.current) {
            fetchItems();
        }
        prevIsOpen.current = isOpen;
    }, [isOpen, fetchItems]);

    // Handle search term changes
    useEffect(() => {
        if (showSearch) {
            if (searchTerm.trim()) {
                debouncedSearch(searchTerm);
            } else {
                setItems([]);
            }
        }
    }, [searchTerm, showSearch, debouncedSearch]);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                dropdownRef.current && !dropdownRef.current.contains(target) &&
                (!menuRef.current || !menuRef.current.contains(target))
            ) {
                setIsOpen(false);
                setShowSearch(false);
                setSearchTerm('');
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Handle escape key
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen) {
                setIsOpen(false);
                setShowSearch(false);
                setSearchTerm('');
                buttonRef.current?.focus();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen]);

    // Focus search input when it becomes visible
    useEffect(() => {
        if (showSearch && isOpen && searchInputRef.current) {
            const timer = setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [showSearch, isOpen]);

    const toggleDropdown = () => {
        if (!disabled) {
            calculateDropdownPosition();
            setIsOpen(!isOpen);
            if (isOpen) {
                setShowSearch(false);
                setSearchTerm('');
            }
        }
    };

    const selectAllItem = useMemo(() => ({
        [idProperty]: '*',
        [displayProperty]: 'Select all'
    }), [idProperty, displayProperty]);

    const isSelectAllEnabled = Boolean(selectAll || selectall);
    const hasInitializedSelectAll = useRef(false);

    useEffect(() => {
        if (isSelectAllEnabled && !hasInitializedSelectAll.current) {
            hasInitializedSelectAll.current = true;
            const hasSelectAll = value.some(v => getItemId(v) === '*');
            if (!hasSelectAll && value.length === 0) {
                onChange([selectAllItem]);
            }
        }
    }, [isSelectAllEnabled, selectAllItem, value, getItemId, onChange]);

    const handleItemToggle = (item: DropdownItem, e: React.MouseEvent) => {
        e.stopPropagation();
        const currentItemId = getItemId(item);
        const isSelected = value.some(v => getItemId(v) === currentItemId);

        let newValues: DropdownItem[];
        if (currentItemId === '*') {
            if (isSelected) {
                newValues = [];
            } else {
                newValues = [item];
            }
        } else {
            if (isSelected) {
                newValues = value.filter(v => getItemId(v) !== currentItemId);
            } else {
                // If "Select all" was selected, remove it and add this item
                const filtered = value.filter(v => getItemId(v) !== '*');
                newValues = [...filtered, item];
            }
        }

        onChange(newValues);
    };

    const handleRemoveTag = (itemToRemove: DropdownItem, e: React.MouseEvent) => {
        e.stopPropagation();
        const idToRemove = getItemId(itemToRemove);
        const newValues = value.filter(v => getItemId(v) !== idToRemove);
        onChange(newValues);
    };

    const handleSearchToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowSearch(true);
        setTimeout(() => {
            searchInputRef.current?.focus();
        }, 50);
    };

    const handleClearSelection = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange([]);
    };

    const getDisplayName = useCallback((item: DropdownItem) => {
        if (typeof item === 'string' || typeof item === 'number') return String(item);
        if (getDisplayText) return getDisplayText(item);
        const displayValue = getNestedProperty(item, displayProperty);
        return displayValue?.toString() || getItemId(item)?.toString() || '';
    }, [getDisplayText, displayProperty, getNestedProperty, getItemId]);

    const getSecondaryDisplayName = (item: DropdownItem) => {
        if (!secondaryDisplayProperty) return null;
        const secondaryValue = getNestedProperty(item, secondaryDisplayProperty);
        return secondaryValue?.toString() || null;
    };

    // Derived properties for UI
    const visibleTags = value.slice(0, maxVisibleTags);
    const hiddenTagCount = value.length - maxVisibleTags;

    return (
        <div className={`space-y-1.5 ${className}`} ref={dropdownRef}>
            {label && (
                <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 dark:text-gray-300">
                    {Icon && <Icon className="w-3 h-3 text-orange-500 dark:text-orange-400" aria-hidden="true" />}
                    {label}
                    {required && <span className='text-red-500 dark:text-red-400'>*</span>}
                </label>
            )}

            <div className="relative">
                <div
                    ref={buttonRef}
                    onClick={toggleDropdown}
                    tabIndex={0}
                    className={`relative w-full cursor-pointer rounded-lg border bg-white dark:bg-gray-900 py-1.5 pl-3 pr-9 text-left text-sm shadow-sm transition-all duration-300
                        ${disabled ? 'opacity-50 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:border-orange-500/50 hover:bg-gray-50 dark:hover:bg-gray-800/80'}
                        ${isOpen ? 'border-orange-500 bg-gray-50 dark:bg-gray-800 ring-2 ring-orange-500/20' : 'border-gray-300 dark:border-gray-700'}
                        focus:outline-none focus:ring-2 focus:ring-orange-500/20 min-h-[${buttonHeight}px] flex items-center`}
                >
                    <div className="flex-1 flex flex-wrap items-center gap-1.5 min-w-0 pr-6">
                        {value.length > 0 ? (
                            <>
                                {visibleTags.map((item) => (
                                    <span
                                        key={getItemId(item)}
                                        className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded text-xs font-medium text-gray-800 dark:text-gray-200"
                                    >
                                        <span className="truncate max-w-[100px]">{getDisplayName(item)}</span>
                                        <button
                                            type="button"
                                            onClick={(e) => handleRemoveTag(item, e)}
                                            disabled={clearDisabled}
                                            className="hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full p-0.5"
                                        >
                                            <X className="w-3 h-3 text-gray-400 hover:text-orange-500 dark:hover:text-orange-400" />
                                        </button>
                                    </span>
                                ))}
                                {hiddenTagCount > 0 && (
                                    <span className="inline-flex items-center px-2 py-0.5 bg-orange-500/10 border border-orange-500/20 rounded text-[10px] font-semibold text-orange-600 dark:text-orange-400">
                                        +{hiddenTagCount} more
                                    </span>
                                )}
                            </>
                        ) : (
                            <span className="block truncate text-gray-400 dark:text-gray-500 leading-tight">{placeholder}</span>
                        )}
                    </div>

                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 gap-1">
                        {value.length > 0 && !clearDisabled && (
                            <button
                                onClick={handleClearSelection}
                                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors mr-0.5"
                                type="button"
                            >
                                <X className="w-3.5 h-3.5 text-gray-400 hover:text-orange-500 dark:hover:text-orange-400" />
                            </button>
                        )}
                        <span className="pointer-events-none flex items-center">
                            <ChevronsUpDown className={`h-4 w-4 text-orange-500 dark:text-orange-400 transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                        </span>
                    </div>
                </div>

                {error && <span className='text-red-500 dark:text-red-400 text-xs mt-1'>{error}</span>}

                {isOpen && createPortal(
                    <div
                        ref={menuRef}
                        className={`fixed z-[99999] max-h-72 overflow-hidden rounded-xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700 animate-in fade-in-0 zoom-in-95 duration-100 ${dropdownPosition === 'top' ? 'origin-bottom' : 'origin-top'}`}
                        style={{ ...menuStyle }}
                    >
                        <div className="flex flex-col h-full">
                            {showSearch ? (
                                <div className="p-2 border-b border-gray-200 dark:border-gray-700/50 bg-gray-50/80 dark:bg-gray-800/80">
                                    <div className="relative">
                                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                        <input
                                            ref={searchInputRef}
                                            type="text"
                                            className="w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg pl-8 pr-3 py-1.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-500 focus:outline-none transition-all"
                                            placeholder={searchInputPlaceholder}
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="p-1.5 border-b border-gray-200 dark:border-gray-700/50">
                                    <button
                                        onClick={handleSearchToggle}
                                        className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-lg transition-colors"
                                        type="button"
                                    >
                                        <Search className="w-3.5 h-3.5" />
                                        {searchPlaceholder}
                                    </button>
                                </div>
                            )}

                            <div className="overflow-y-auto custom-scroll overflow-x-hidden flex-1 max-h-[220px]">
                                {isLoading && items.length === 0 ? (
                                    <div className="p-6 text-center">
                                        <div className="w-5 h-5 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin mx-auto mb-2" />
                                        <span className="text-[10px] text-gray-500 dark:text-gray-400 font-medium uppercase tracking-widest">Loading...</span>
                                    </div>
                                ) : items.length > 0 ? (
                                    <>
                                        {!searchTerm.trim() && (
                                            (() => {
                                                const isSelected = value.some(v => getItemId(v) === '*');
                                                return (
                                                    <div
                                                        key="*"
                                                        onClick={(e) => handleItemToggle(selectAllItem, e)}
                                                        className={`w-full relative cursor-pointer select-none py-2 px-3 flex items-start gap-2.5 text-sm text-left transition-all duration-150 hover:bg-gray-100 dark:hover:bg-gray-700/50 ${isSelected ? "bg-orange-500/10 dark:bg-orange-500/5" : ""}`}
                                                    >
                                                        <div className="flex-shrink-0 pt-0.5">
                                                            {isSelected ? (
                                                                <CheckSquare className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" />
                                                            ) : (
                                                                <Square className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                                                            )}
                                                        </div>

                                                        <div className="flex items-center gap-2.5 flex-1 min-w-0">
                                                            {Icon && (
                                                                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 bg-gray-100 dark:bg-gray-700">
                                                                    <Icon className={`w-2.5 h-2.5 ${isSelected ? 'text-orange-500 dark:text-orange-400' : 'text-gray-500 dark:text-gray-400'}`} />
                                                                </div>
                                                            )}
                                                            <div className="min-w-0 flex-1">
                                                                <span className={`block truncate ${isSelected ? "text-orange-600 dark:text-white font-medium" : "text-gray-800 dark:text-gray-300"}`}>
                                                                    Select all
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })()
                                        )}
                                        {items.map((item) => {
                                            const itemId = getItemId(item);
                                            const isSelected = value.some(v => getItemId(v) === itemId);
                                            const displayName = getDisplayName(item);
                                            const secondaryDisplayName = getSecondaryDisplayName(item);

                                            return (
                                                <div
                                                    key={itemId}
                                                    onClick={(e) => handleItemToggle(item, e)}
                                                    className={`w-full relative cursor-pointer select-none py-2 px-3 flex items-start gap-2.5 text-sm text-left transition-all duration-150 hover:bg-gray-100 dark:hover:bg-gray-700/50 ${isSelected ? "bg-orange-500/10 dark:bg-orange-500/5" : ""}`}
                                                >
                                                    <div className="flex-shrink-0 pt-0.5">
                                                        {isSelected ? (
                                                            <CheckSquare className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" />
                                                        ) : (
                                                            <Square className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                                                        )}
                                                    </div>

                                                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                                                        {Icon && (
                                                            <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 bg-gray-100 dark:bg-gray-700">
                                                                <Icon className={`w-2.5 h-2.5 ${isSelected ? 'text-orange-500 dark:text-orange-400' : 'text-gray-500 dark:text-gray-400'}`} />
                                                            </div>
                                                        )}
                                                        <div className="min-w-0 flex-1">
                                                            <span className={`block truncate ${isSelected ? "text-orange-600 dark:text-white font-medium" : "text-gray-800 dark:text-gray-300"}`}>
                                                                {displayName}
                                                            </span>
                                                            {secondaryDisplayProperty && secondaryDisplayName && (
                                                                <span className="block truncate text-[10px] text-gray-500 dark:text-gray-200 mt-0.5">
                                                                    {secondaryDisplayName}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </>
                                ) : (
                                    <div className="p-6 text-center">
                                        <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                                            {showSearch && searchTerm.trim() === '' ? 'Type to search...' :
                                                showSearch ? 'No results found' : 'No data available'}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
            </div>
        </div>
    );
});

export default MultiSelectApiDropdownWithAll;
